/**
 * "Linked Components" card on the equipment detail — child components installed
 * in the equipment (legacy `getItems({parent_id, item_type:'component'})`).
 * Empty → the legacy "No components installed" note; otherwise a compact table
 * with a per-row "Return to Warehouse" action (POST /components/return).
 */
import { useState } from 'react';
import { Cpu, RotateCcw } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardBody, Chip, Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import type { Item } from '@/api/capacities';
import { useReturnComponents, useLocations } from '@/api/capacities';
import { TableLink } from '@/components/table-link';
import { itemStatusCell } from './items-status';

interface ReturnDialogProps {
  component: Item;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function ReturnDialog({ component, open, onOpenChange }: ReturnDialogProps) {
  const [locationId, setLocationId] = useState('');
  const locations = useLocations({ page_size: 100 });
  const returnMutation = useReturnComponents();

  const warehouseLocations = (locations.data?.results ?? []).filter(
    (l) => l.type === 'warehouse',
  );

  const handleSubmit = () => {
    if (!locationId) return;
    returnMutation.mutate(
      { component_ids: [component.id], location_id: locationId },
      {
        onSuccess: () => {
          setLocationId('');
          onOpenChange(false);
        },
      },
    );
  };

  const handleOpenChange = (next: boolean) => {
    if (!returnMutation.isPending) {
      if (!next) setLocationId('');
      onOpenChange(next);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Return to Warehouse</DialogTitle>
        </DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          <Alert tone="warning" title="Live action">
            This returns <strong>{component.name}</strong> to the selected warehouse location immediately.
          </Alert>
          {returnMutation.isError ? (
            <Alert tone="danger" title="Return failed">
              Could not return the component. Please try again.
            </Alert>
          ) : null}
          <Field label="Warehouse location" required>
            {(p) => (
              <Select value={locationId} onValueChange={setLocationId}>
                <SelectTrigger {...p}>
                  <SelectValue placeholder={locations.isLoading ? 'Loading…' : 'Select a warehouse location'} />
                </SelectTrigger>
                <SelectContent>
                  {warehouseLocations.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" disabled={returnMutation.isPending} onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!locationId || returnMutation.isPending}
            loading={returnMutation.isPending}
            onClick={handleSubmit}
          >
            Return to Warehouse
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LinkedComponentsCard({ components }: { components: Item[] }) {
  const [returning, setReturning] = useState<Item | null>(null);

  const columns: ColumnDef<Item, unknown>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <TableLink to={`/capacities/components/${row.original.id}`} title={row.original.name}>
          {row.original.name}
        </TableLink>
      ),
    },
    {
      id: 'ref',
      header: 'Ref',
      cell: ({ row }) => (
        <span style={{ fontFamily: 'var(--vn-font-mono, monospace)' }}>{row.original.ref || '—'}</span>
      ),
    },
    {
      id: 'category',
      header: 'Category',
      cell: ({ row }) => <span style={{ textTransform: 'capitalize' }}>{row.original.category || '—'}</span>,
    },
    {
      id: 'condition',
      header: 'Condition',
      cell: ({ row }) => <span style={{ textTransform: 'capitalize' }}>{row.original.condition || '—'}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => itemStatusCell(row.original.status),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          startIcon={<RotateCcw size={13} />}
          onClick={() => setReturning(row.original)}
          title="Return to warehouse"
        >
          Return
        </Button>
      ),
    },
  ];

  return (
    <>
      <Card flat>
        <CardHeader>
          <CardTitle>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
              <Cpu size={16} />
              Linked Components
              {components.length > 0 ? <Chip tone="neutral">{components.length}</Chip> : null}
            </span>
          </CardTitle>
        </CardHeader>
        <CardBody>
          <DataTable
            columns={columns}
            data={components}
            getRowId={(r) => r.id}
            enableSorting={false}
            emptyState={
              <EmptyState size="sm" title="No components installed" description="No components are installed in this equipment." />
            }
          />
        </CardBody>
      </Card>

      {returning ? (
        <ReturnDialog
          component={returning}
          open={!!returning}
          onOpenChange={(open) => { if (!open) setReturning(null); }}
        />
      ) : null}
    </>
  );
}
